// Sprint E · Task 5 — score-breakdown popover.
// Click the score cell to open. Shows each component's raw score, weight,
// and contribution; lists top 3 drivers (positive) and top 3 deductions
// (negative — factors where the sub-score landed well below 1.0).
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

export function ScoreCell({ pct, rating, breakdown, color, suffixClass }) {
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!anchor) return;
    const close = () => setAnchor(null);
    const onClick = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.score-pop')) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);

  const onToggle = (e) => {
    e.stopPropagation();
    if (anchor) { setAnchor(null); return; }
    setAnchor(e.currentTarget.getBoundingClientRect());
  };

  return (
    <>
      <button ref={btnRef} type="button" className="score-cell-btn"
              onClick={onToggle} aria-label="Score breakdown"
              style={{ color }}>
        {pct}<span className={suffixClass}>/100</span>
        <div className="pp-score-rating">{rating} ▾</div>
      </button>
      {anchor && breakdown && (
        <ScoreBreakdownPopover breakdown={breakdown} pct={pct} rating={rating}
                                color={color} anchor={anchor} />
      )}
    </>
  );
}

function ScoreBreakdownPopover({ breakdown, pct, rating, color, anchor }) {
  const POP_W = 380;
  const margin = 8;
  // Viewport-aware placement (audit 2026-05-28): when there isn't
  // enough room below the anchor (Compare-table score cells are near
  // the bottom of the page on small screens), flip the popover above
  // the anchor. Either way, cap maxHeight and enable scroll so the
  // full breakdown is always reachable — previously the tail (Top
  // drivers / Penalties / Not scored) was cut off invisibly.
  const left = Math.min(
    Math.max(margin, anchor.left + anchor.width / 2 - POP_W / 2),
    window.innerWidth - POP_W - margin,
  );
  const spaceBelow = window.innerHeight - anchor.bottom - margin;
  const spaceAbove = anchor.top - margin;
  const placeAbove = spaceBelow < 280 && spaceAbove > spaceBelow;
  const posStyle = placeAbove
    ? { bottom: window.innerHeight - anchor.top + 6, maxHeight: spaceAbove - 12 }
    : { top: anchor.bottom + 6,                       maxHeight: spaceBelow - 12 };

  const { components, factors } = breakdown;

  // Drivers = factors where sub-score >= 0.7 (good or great).
  // Deductions = factors with sub-score <= 0.5 (penalty territory).
  const drivers = factors
    .filter(f => f.scoreVal != null && f.scoreVal >= 0.7)
    .sort((a, b) => (b.weight * b.scoreVal) - (a.weight * a.scoreVal))
    .slice(0, 3);
  const deductions = factors
    .filter(f => f.scoreVal != null && f.scoreVal <= 0.5)
    .sort((a, b) => (a.scoreVal * a.weight) - (b.scoreVal * b.weight))
    .slice(0, 3);
  const missing = factors.filter(f => f.scoreVal == null).map(f => f.label);

  return createPortal(
    <div className="score-pop"
         style={{ left, width: POP_W, overflowY: 'auto', ...posStyle }}
         role="dialog" aria-label="Score breakdown">
      <div className="score-pop-header">
        <span className="score-pop-total" style={{ color }}>{pct}<span className="score-pop-tot-suffix">/100</span></span>
        <span className="score-pop-rating">{rating}</span>
      </div>

      <div className="score-pop-section">
        <div className="score-pop-section-title">Component breakdown</div>
        {components.map(c => (
          <ComponentBar key={c.name} comp={c} />
        ))}
      </div>

      {drivers.length > 0 && (
        <div className="score-pop-section">
          <div className="score-pop-section-title">Top drivers</div>
          {drivers.map(f => (
            <div key={f.key} className="score-pop-factor score-pop-factor-good">
              <span className="score-pop-factor-mark">✓</span>
              <span className="score-pop-factor-label">{f.label}</span>
              <span className="score-pop-factor-value">{f.valueText || '—'}</span>
              <span className="score-pop-factor-pts">+{Math.round(f.contribution)}</span>
            </div>
          ))}
        </div>
      )}

      {deductions.length > 0 && (
        <div className="score-pop-section">
          <div className="score-pop-section-title">Penalties</div>
          {deductions.map(f => (
            <div key={f.key} className="score-pop-factor score-pop-factor-bad">
              <span className="score-pop-factor-mark">✗</span>
              <span className="score-pop-factor-label">{f.label}</span>
              <span className="score-pop-factor-value">{f.valueText || '—'}</span>
              <span className="score-pop-factor-pts">−{Math.round(f.weight - f.contribution)}</span>
            </div>
          ))}
        </div>
      )}

      {missing.length > 0 && (
        <div className="score-pop-section score-pop-missing">
          <div className="score-pop-section-title">Not scored ({missing.length})</div>
          <div className="score-pop-missing-list">{missing.join(' · ')}</div>
        </div>
      )}
    </div>,
    document.body,
  );
}

function ComponentBar({ comp }) {
  const fillPct = comp.raw != null ? comp.raw : 0;
  const barColor = fillPct >= 75 ? '#22c55e' : fillPct >= 50 ? '#f59e0b' : fillPct > 0 ? '#ef4444' : '#475569';
  return (
    <div className="score-pop-comp">
      <div className="score-pop-comp-row">
        <span className="score-pop-comp-name">{comp.name}</span>
        <span className="score-pop-comp-raw">
          {comp.raw != null ? comp.raw : '—'} × {Math.round(comp.weightPct * 100)}%
          <span className="score-pop-comp-contrib"> = {comp.contribution.toFixed(1)}</span>
        </span>
      </div>
      <div className="score-pop-comp-bar">
        <div className="score-pop-comp-bar-fill"
             style={{ width: `${fillPct}%`, background: barColor }} />
      </div>
    </div>
  );
}
